/**
 * Shop metafield for AgeShield template config (read by theme / storefront integrations).
 * Uses Admin API via App Bridge graphqlCall — same pattern as Admin_react shop metafields.
 */
import { graphqlCall } from "./Common";

export const AGE_TEMPLATE_METAFIELD_NAMESPACE = "ageshield";

/** Fixed keys read by `ageshield.liquid` + `ageshield-storefront.js` (split path). */
export const AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP = "popup_config";
export const AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT = "checkout_config";
/** JSON array of `template_{id}` keys — Liquid merges these into the storefront catalog for country/language matching. */
export const AGE_SHIELD_SHOP_METAFIELD_KEY_TEMPLATE_INDEX = "template_index";

/** Shopify shop metafield keys: alphanumeric, underscore, hyphen; max 64 chars. */
export function ageTemplateMetafieldKey(templateId) {
  const safe = String(templateId ?? "").replace(/[^a-zA-Z0-9_-]/g, "_");
  const key = `template_${safe || "unknown"}`;
  return key.length <= 64 ? key : key.slice(0, 64);
}

function shopGidFromNumericId(shopifyId) {
  const id = typeof shopifyId === "string" ? parseInt(shopifyId, 10) : shopifyId;
  if (id == null || Number.isNaN(id)) {
    throw new Error("Invalid shopify_id for Shop metafield owner");
  }
  return `gid://shopify/Shop/${id}`;
}

const SHOP_OWNER_TYPE = "SHOP";

function throwIfGraphqlErrors(response, label) {
  const err = response?.errors?.[0]?.message;
  if (err) {
    throw new Error(`${label}: ${err}`);
  }
}

/**
 * Shop JSON metafields are invisible in Online Store Liquid unless the metafield definition
 * grants Storefront access (e.g. PUBLIC_READ). This syncs definition for one key under `ageshield`.
 * @param {string} key — e.g. template_1
 */
export async function ensureAgeShieldShopJsonMetafieldStorefrontAccess(key) {
  const listQuery = {
    query: `
      query AgeShieldMfDefList($ownerType: MetafieldOwnerType!, $namespace: String!, $key: String!) {
        metafieldDefinitions(ownerType: $ownerType, namespace: $namespace, key: $key, first: 5) {
          edges {
            node {
              id
              access {
                storefront
              }
            }
          }
        }
      }
    `,
    variables: {
      ownerType: SHOP_OWNER_TYPE,
      namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
      key,
    },
  };

  const listed = await graphqlCall(listQuery);
  throwIfGraphqlErrors(listed, "metafieldDefinitions");
  const node = listed?.data?.metafieldDefinitions?.edges?.[0]?.node;
  const storefront = node?.access?.storefront;
  const hasPublicStorefront =
    storefront === "PUBLIC_READ" ||
    storefront === "PUBLIC_READ_WRITE" ||
    (typeof storefront === "string" && storefront.includes("PUBLIC_READ"));

  if (node?.id && hasPublicStorefront) {
    return;
  }

  if (node?.id) {
    const up = await graphqlCall({
      query: `
        mutation AgeShieldMfDefUpdate($definition: MetafieldDefinitionUpdateInput!) {
          metafieldDefinitionUpdate(definition: $definition) {
            updatedDefinition {
              id
            }
            userErrors {
              code
              field
              message
            }
          }
        }
      `,
      variables: {
        definition: {
          name: `AgeShield ${key}`,
          namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
          key,
          ownerType: SHOP_OWNER_TYPE,
          access: { storefront: "PUBLIC_READ" },
        },
      },
    });
    throwIfGraphqlErrors(up, "metafieldDefinitionUpdate");
    const ue = up?.data?.metafieldDefinitionUpdate?.userErrors;
    if (ue?.length) {
      throw new Error(ue[0]?.message || "metafieldDefinitionUpdate user error");
    }
    return;
  }

  const cr = await graphqlCall({
    query: `
      mutation AgeShieldMfDefCreate($definition: MetafieldDefinitionInput!) {
        metafieldDefinitionCreate(definition: $definition) {
          createdDefinition {
            id
          }
          userErrors {
            code
            field
            message
          }
        }
      }
    `,
    variables: {
      definition: {
        name: `AgeShield ${key}`,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key,
        description: "Age verification template JSON (read by AgeShield theme app embed).",
        type: "json",
        ownerType: SHOP_OWNER_TYPE,
        access: { storefront: "PUBLIC_READ" },
      },
    },
  });
  throwIfGraphqlErrors(cr, "metafieldDefinitionCreate");
  const crUe = cr?.data?.metafieldDefinitionCreate?.userErrors;
  if (crUe?.length) {
    throw new Error(crUe[0]?.message || "metafieldDefinitionCreate user error");
  }
}

/**
 * Origin for POST /api/public/verification-events (no trailing slash). Stored on `popup_config` metafield
 * so storefront analytics work without a separate theme field (embed URL still overrides).
 */
function publicVerificationEventsApiBaseFromEnv() {
  const explicit = String(process.env.REACT_APP_VERIFICATION_EVENTS_API_BASE || "")
    .trim()
    .replace(/\/+$/, "");
  if (explicit) return explicit;
  const api = String(process.env.REACT_APP_API_URL || "")
    .trim()
    .replace(/\/+$/, "");
  if (!api) return "";
  return api.replace(/\/api\/?$/i, "").replace(/\/+$/, "");
}

/**
 * Same slices as `node_ageshield` `splitConfigForColumns` → DB `popup_config_json` / `checkout_config_json`.
 * Theme extension reads these via `shop.metafields.ageshield.popup_config` / `checkout_config`.
 * @param {{ config?: object }} payload — from `buildTemplatePersistPayload`
 * @param {string|number} templateId — echoed in shop slices for storefront / checkout analytics.
 */
function shopMetafieldSlicesFromPayload(payload, templateId) {
  const cfg = payload && payload.config && typeof payload.config === "object" ? payload.config : null;
  if (!cfg) return null;
  const version = cfg.version != null ? cfg.version : 1;
  const tid = templateId != null ? String(templateId) : "";
  const eventsBase = publicVerificationEventsApiBaseFromEnv();
  return {
    popupSlice: {
      version,
      popupEnabled: cfg.popupEnabled,
      popup: cfg.popup,
      ...(tid ? { templateId: tid } : {}),
      ...(eventsBase ? { eventsApiBase: eventsBase } : {}),
    },
    checkoutSlice: {
      checkoutEnabled: cfg.checkoutEnabled,
      checkout: cfg.checkout,
      ...(tid ? { templateId: tid } : {}),
    },
  };
}

/**
 * @param {{ id: number|string, country_code?: string, language_code?: string, config?: object }[]} templates
 */
function buildTemplateIndexKeysArray(templates) {
  const keys = (templates || []).map((t) => ageTemplateMetafieldKey(t.id)).filter(Boolean);
  const uniq = [...new Set(keys)];
  uniq.sort((a, b) => {
    const na = parseInt(String(a).replace(/^template_/i, ""), 10);
    const nb = parseInt(String(b).replace(/^template_/i, ""), 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
  return uniq;
}

/**
 * @param {{ id: number|string, country_code?: string, language_code?: string, config?: object }[]} templates
 */
function buildPopupConfigV2(templates) {
  const eventsBase = publicVerificationEventsApiBaseFromEnv();
  const entries = (templates || []).map((t) => {
    const cfg = t.config && typeof t.config === "object" ? t.config : {};
    const version = cfg.version != null ? cfg.version : 1;
    return {
      templateId: String(t.id),
      country_code: String(t.country_code || "").trim().toUpperCase().slice(0, 8),
      language_code: String(t.language_code || "").trim().toLowerCase().slice(0, 16),
      popupEnabled: !!cfg.popupEnabled,
      version,
      popup: cfg.popup && typeof cfg.popup === "object" ? cfg.popup : {},
      ...(eventsBase ? { eventsApiBase: eventsBase } : {}),
    };
  });
  return { version: 2, entries };
}

/**
 * @param {{ id: number|string, country_code?: string, language_code?: string, config?: object }[]} templates
 */
function buildCheckoutConfigV2(templates) {
  const entries = (templates || []).map((t) => {
    const cfg = t.config && typeof t.config === "object" ? t.config : {};
    return {
      templateId: String(t.id),
      country_code: String(t.country_code || "").trim().toUpperCase().slice(0, 8),
      language_code: String(t.language_code || "").trim().toLowerCase().slice(0, 16),
      checkoutEnabled: !!cfg.checkoutEnabled,
      checkout: cfg.checkout && typeof cfg.checkout === "object" ? cfg.checkout : {},
    };
  });
  return { version: 2, entries };
}

/**
 * Create or update Shop JSON metafields under `ageshield` (namespace):
 * - `template_{id}` — full payload + templateId/savedAt (legacy + single-source mirror)
 * - `popup_config` — { version, popupEnabled, popup, templateId? } (same shape extension expects when split)
 * - `checkout_config` — v2 `{ version: 2, entries: [...] }` when `allTemplatesDetailed` is passed (country/language aware); else legacy single slice
 * - `template_index` — JSON array of `template_{id}` keys for Liquid catalog (only when `allTemplatesDetailed` passed)
 *
 * @param {string|number} shopifyId - Numeric Shopify shop id from install `shop_info.shopify_id`
 * @param {string|number} templateId
 * @param {object} payload - Same shape as template API body (name, country_code, language_code, config)
 * @param {Array<{ id: number|string, country_code?: string, language_code?: string, config?: object }>|null} [allTemplatesDetailed] — full templates from API; enables multi-locale shop metafields
 */
export async function createOrUpdateAgeTemplateShopMetafield(shopifyId, templateId, payload, allTemplatesDetailed) {
  const ownerId = shopGidFromNumericId(shopifyId);
  const templateKey = ageTemplateMetafieldKey(templateId);
  const mergedValue = JSON.stringify({
    templateId: String(templateId),
    savedAt: new Date().toISOString(),
    ...payload,
  });

  const slices = shopMetafieldSlicesFromPayload(payload, templateId);
  const useMulti =
    Array.isArray(allTemplatesDetailed) && allTemplatesDetailed.length > 0;
  const keysToEnsure = [
    templateKey,
    AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP,
    AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT,
  ];
  if (useMulti) {
    keysToEnsure.push(AGE_SHIELD_SHOP_METAFIELD_KEY_TEMPLATE_INDEX);
  }
  for (let i = 0; i < keysToEnsure.length; i++) {
    await ensureAgeShieldShopJsonMetafieldStorefrontAccess(keysToEnsure[i]);
  }

  const metafields = [
    {
      ownerId,
      namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
      key: templateKey,
      value: mergedValue,
      type: "json",
    },
  ];

  if (useMulti) {
    const popupV2 = buildPopupConfigV2(allTemplatesDetailed);
    const checkoutV2 = buildCheckoutConfigV2(allTemplatesDetailed);
    const indexKeys = buildTemplateIndexKeysArray(allTemplatesDetailed);
    metafields.push(
      {
        ownerId,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key: AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP,
        value: JSON.stringify(popupV2),
        type: "json",
      },
      {
        ownerId,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key: AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT,
        value: JSON.stringify(checkoutV2),
        type: "json",
      },
      {
        ownerId,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key: AGE_SHIELD_SHOP_METAFIELD_KEY_TEMPLATE_INDEX,
        value: JSON.stringify(indexKeys),
        type: "json",
      },
    );
  } else if (slices) {
    metafields.push(
      {
        ownerId,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key: AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP,
        value: JSON.stringify(slices.popupSlice),
        type: "json",
      },
      {
        ownerId,
        namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
        key: AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT,
        value: JSON.stringify(slices.checkoutSlice),
        type: "json",
      },
    );
  }

  const query = {
    query: `
      mutation AgeShieldSetTemplateMetafield($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields {
            id
            namespace
            key
            type
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: { metafields },
  };

  const response = await graphqlCall(query);

  if (response.errors?.length) {
    throw new Error(response.errors[0]?.message || "Metafield GraphQL error");
  }
  const userErrors = response.data?.metafieldsSet?.userErrors;
  if (userErrors?.length) {
    throw new Error(userErrors[0]?.message || "Metafield user error");
  }
  return response.data?.metafieldsSet?.metafields ?? null;
}

function templatePayloadForMetafield(template) {
  return {
    name: template.name,
    country_code: template.country_code,
    language_code: template.language_code,
    config: template.config,
  };
}

/**
 * Re-push every template from the app DB to Shopify shop metafields (storefront-visible).
 * Used after reinstall so merchants do not need to click Save in the template editor.
 */
export async function syncAllAgeShieldShopMetafields(shopifyId, templatesDetailed) {
  const templates = Array.isArray(templatesDetailed) ? templatesDetailed.filter(Boolean) : [];
  if (!templates.length) return { synced: 0 };

  const ownerId = shopGidFromNumericId(shopifyId);
  const savedAt = new Date().toISOString();
  const indexKeys = buildTemplateIndexKeysArray(templates);
  const keysToEnsure = [
    ...indexKeys,
    AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP,
    AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT,
    AGE_SHIELD_SHOP_METAFIELD_KEY_TEMPLATE_INDEX,
  ];

  for (let i = 0; i < keysToEnsure.length; i++) {
    await ensureAgeShieldShopJsonMetafieldStorefrontAccess(keysToEnsure[i]);
  }

  const metafields = templates.map((t) => ({
    ownerId,
    namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
    key: ageTemplateMetafieldKey(t.id),
    value: JSON.stringify({
      templateId: String(t.id),
      savedAt,
      ...templatePayloadForMetafield(t),
    }),
    type: "json",
  }));

  metafields.push(
    {
      ownerId,
      namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
      key: AGE_SHIELD_SHOP_METAFIELD_KEY_POPUP,
      value: JSON.stringify(buildPopupConfigV2(templates)),
      type: "json",
    },
    {
      ownerId,
      namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
      key: AGE_SHIELD_SHOP_METAFIELD_KEY_CHECKOUT,
      value: JSON.stringify(buildCheckoutConfigV2(templates)),
      type: "json",
    },
    {
      ownerId,
      namespace: AGE_TEMPLATE_METAFIELD_NAMESPACE,
      key: AGE_SHIELD_SHOP_METAFIELD_KEY_TEMPLATE_INDEX,
      value: JSON.stringify(indexKeys),
      type: "json",
    },
  );

  const query = {
    query: `
      mutation AgeShieldSetTemplateMetafield($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields {
            id
            namespace
            key
            type
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: { metafields },
  };

  const response = await graphqlCall(query);
  if (response.errors?.length) {
    throw new Error(response.errors[0]?.message || "Metafield GraphQL error");
  }
  const userErrors = response.data?.metafieldsSet?.userErrors;
  if (userErrors?.length) {
    throw new Error(userErrors[0]?.message || "Metafield user error");
  }
  return { synced: templates.length };
}
